// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useState } from 'react';
import { Typography, TableRow, TableCell, CircularProgress, Snackbar, Alert } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import InfoIcon from '@mui/icons-material/Info';
import moment from 'moment-timezone';
import { formatOrderPromotionValue } from 'Utils/promotion';
import { pdf } from '@react-pdf/renderer';
import { GetProjectDetailsRoute } from 'Utils/routes';
import { Link as RouterLink } from 'react-router-dom';
import downloadIcon from 'Assets/download-icon.png';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { WideLightTooltip } from 'Components/Tooltip';
import PDF from 'Features/order/PDF';
import { getCustomerOrderStatus } from 'Utils/orderStatus';
import OrderSummary from './orderSummary';
import stylesDef from './styles';
import { GetPdfDataThunk } from 'Features/order/orderThunk';
import { useFlags } from 'launchdarkly-react-client-sdk';
import getTotalChargedCredits from 'Utils/totalChargedCreditsUtil';

moment.tz.setDefault('America/New_York');

interface ComponentProps {
  order: IOrder;
}

export const OrderListItem: React.FC<ComponentProps> = React.memo(({ order }) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const { orgTeams } = useAppSelector((state) => state.account);
  const team = orgTeams?.find((t) => t._id === order.team);
  const { salesNoDollars, salesNoCredits } = useFlags();

  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfFileName, setPdfFileName] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(false);

  const saveBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Invoice PDFs are built on demand (AUT-6351) — nothing is fetched or rendered at page load.
  // The built blob stays in row state so repeat downloads are instant while the row is mounted.
  const handleDownloadInvoice = async () => {
    window?.pendo?.track('Invoice downloaded', { projectId: order?.ownerProject });
    if (pdfBlob) {
      saveBlob(pdfBlob, pdfFileName);
      return;
    }
    setPdfLoading(true);
    setPdfError(false);
    try {
      const result = await dispatch(GetPdfDataThunk(order._id));
      if (!result.success) {
        throw new Error(result.message);
      }
      const pdfDataObject: IPdf = {
        ...result.data.pdfData,
        type: 'Invoice',
      };
      const blob = await pdf(<PDF data={pdfDataObject} />).toBlob();
      const fileName = `${pdfDataObject?.projectName}_invoice.pdf`;
      setPdfBlob(blob);
      setPdfFileName(fileName);
      saveBlob(blob, fileName);
    } catch (error) {
      setPdfError(true);
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <TableRow key={order._id}>
      <TableCell sx={styles.tableCell} component="th" scope="row">
        {order.name}
      </TableCell>
      <TableCell sx={styles.tableCell} component="th" scope="row">
        {order.referenceId || "N/A"}
      </TableCell>
      <TableCell sx={styles.tableCell} component="th" scope="row">
        {team?.name || "N/A"}
      </TableCell>
      <TableCell sx={styles.tableCell} component="th" scope="row">
        {/* If orderDate is undefined display N/A */}
        {order.orderDate
          ? moment(order.orderDate).format("MMMM D, YYYY")
          : "N/A"}
      </TableCell>
      <TableCell sx={styles.tableCell} component="th" scope="row">
        {/* In-flight orders list here now (AUT-6526); a row without a status renders a dash, never a guess */}
        {getCustomerOrderStatus(order) ?? '—'}
      </TableCell>
      <TableCell sx={styles.tableCell} component="th" scope="row">
        {order.projectType === 'utilities' ? "N/A" : order.acreage.toFixed(2)}
      </TableCell>
      <TableCell sx={styles.tableCell} component="th" scope="row">
        {order.projectType !== 'utilities' ? "N/A" : order.kmlLength?.toFixed(2)}
      </TableCell>
      {!salesNoCredits && (
        <TableCell sx={styles.tableCell} component="th" scope="row">
          <Box sx={styles.info}>
            {(order.expressAICreditAcreage ? order.expressAICreditAcreage : getTotalChargedCredits(order)).toFixed(2)}
            <WideLightTooltip
              placement="bottom"
              title={<OrderSummary order={order} />}
            >
              <InfoIcon sx={styles.icon} />
            </WideLightTooltip>
          </Box>
        </TableCell>
      )}
      {!salesNoDollars && (
        <TableCell sx={styles.tableCell} component="th" scope="row">
          <Box sx={styles.info}>
            {order.costToCustomer ? `$${order.costToCustomer.toFixed(2)}` : "N/A"}
            <WideLightTooltip
              placement="bottom"
              interactive
              title={
                <Box>
                  <Box sx={styles.divGrid}>
                    <Typography variant="body1" sx={styles.text}>
                      Project cost
                    </Typography>
                    <Typography sx={styles.text} variant="body1">
                      {order.costToCustomer >= 0
                        ? `$${order.costToCustomer.toFixed(2)}`
                        : "N/A"}
                    </Typography>
                  </Box>
                  {order?.salesTaxAmount > 0 && (
                    <Box sx={styles.divGrid}>
                      <Typography variant="body1" sx={styles.text}>
                        Sales tax
                      </Typography>
                      <Typography sx={styles.text} variant="body1">
                        {`$${order.salesTaxAmount.toFixed(2)}`}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={styles.divGrid}>
                    <Typography variant="body1" sx={styles.text}>
                      Additional cost
                    </Typography>
                    <Typography sx={styles.text} variant="body1">
                      {order.paidForThisProject >= 0
                        ? `$${order.paidForThisProject.toFixed(2)}`
                        : "N/A"}
                    </Typography>
                  </Box>
                  {order.promotion && (
                    <Box sx={styles.divGrid}>
                      <Typography variant="body1" sx={styles.text}>
                        {order.promotion.displayText}
                      </Typography>
                      <Typography sx={styles.text} variant="body1">
                        {formatOrderPromotionValue(order.promotion, salesNoDollars)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              }
            >
              <InfoIcon sx={styles.icon} />
            </WideLightTooltip>
          </Box>
        </TableCell>
      )}
      <TableCell sx={styles.iconTableCell} component="th" scope="row">
        {pdfLoading ? (
          <CircularProgress size={24} sx={styles.buttonProgress} />
        ) : (
          <Box sx={styles.downloadButton} onClick={handleDownloadInvoice}>
            <img src={downloadIcon} alt="download invoice" />
          </Box>
        )}
        <Snackbar open={pdfError} autoHideDuration={6000} onClose={() => setPdfError(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity="error">Unable to build the invoice PDF. Please try again.</Alert>
        </Snackbar>
      </TableCell>
      {!_ADMIN_ && (
        <TableCell sx={styles.tableCell} component="th" scope="row">
          <RouterLink
            to={GetProjectDetailsRoute(order.ownerProject)}
            css={styles.routerLink}
            title="View Project"
          >
            <Typography variant="h6">View Project</Typography>
          </RouterLink>
        </TableCell>
      )}
    </TableRow>
  );
});

export default OrderListItem;
